// Realtime optimization utilities
import { supabase } from '@/integrations/supabase/client';

interface BatchUpdateConfig {
  maxBatchSize: number;
  batchTimeoutMs: number;
  priority: 'high' | 'medium' | 'low';
}

class BatchUpdateManager {
  private static instance: BatchUpdateManager;
  private pendingUpdates = new Map<string, {
    updates: any[];
    timer: NodeJS.Timeout;
    callback: (updates: any[]) => void;
    config: BatchUpdateConfig;
  }>();
  
  // Performance metrics
  private metrics = {
    totalBatches: 0,
    totalUpdates: 0,
    batchesByKey: new Map<string, number>(),
    updatesSaved: 0, // How many individual renders were prevented
    averageBatchSize: 0,
    lastBatchTimestamp: 0
  };

  static getInstance(): BatchUpdateManager {
    if (!this.instance) {
      this.instance = new BatchUpdateManager();
    }
    return this.instance;
  }
  
  getMetrics() {
    return {
      ...this.metrics,
      activeBatches: this.pendingUpdates.size,
      batchesByKey: Object.fromEntries(this.metrics.batchesByKey)
    };
  }
  
  resetMetrics() {
    this.metrics = {
      totalBatches: 0,
      totalUpdates: 0,
      batchesByKey: new Map(),
      updatesSaved: 0,
      averageBatchSize: 0,
      lastBatchTimestamp: 0
    };
  }

  addUpdate(
    key: string,
    update: any,
    callback: (updates: any[]) => void,
    config: BatchUpdateConfig = {
      maxBatchSize: 10,
      batchTimeoutMs: 1000,
      priority: 'medium'
    }
  ) {
    const existing = this.pendingUpdates.get(key);
    
    if (existing) {
      existing.updates.push(update);
      this.metrics.totalUpdates++;
      this.metrics.updatesSaved++; // Each batched update saves a render
      
      // If batch is full, process immediately
      if (existing.updates.length >= config.maxBatchSize) {
        this.processBatch(key);
      }
    } else {
      // Create new batch
      const timer = setTimeout(() => {
        this.processBatch(key);
      }, config.batchTimeoutMs);

      this.pendingUpdates.set(key, {
        updates: [update],
        timer,
        callback,
        config
      });
      
      this.metrics.totalUpdates++;
    }
  }

  private processBatch(key: string) {
    const batch = this.pendingUpdates.get(key);
    if (!batch) return;

    // Clear timer
    clearTimeout(batch.timer);
    
    // Update metrics
    const batchSize = batch.updates.length;
    this.metrics.totalBatches++;
    this.metrics.batchesByKey.set(key, (this.metrics.batchesByKey.get(key) || 0) + 1);
    this.metrics.averageBatchSize = this.metrics.totalUpdates / this.metrics.totalBatches;
    this.metrics.lastBatchTimestamp = Date.now();

    // Log batch processing in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 BatchUpdateManager: Processing batch "${key}" with ${batchSize} updates`, {
        totalBatches: this.metrics.totalBatches,
        totalUpdates: this.metrics.totalUpdates,
        updatesSaved: this.metrics.updatesSaved,
        avgBatchSize: this.metrics.averageBatchSize.toFixed(2)
      });
    }

    // Process updates
    try {
      batch.callback(batch.updates);
    } catch (error) {
      console.error(`Error processing batch for ${key}:`, error);
    }

    // Remove batch
    this.pendingUpdates.delete(key);
  }

  cleanup() {
    this.pendingUpdates.forEach((batch, key) => {
      clearTimeout(batch.timer);
      this.pendingUpdates.delete(key);
    });
  }
}

// Performance tracking for realtime operations
export class RealtimePerformanceMonitor {
  private static metrics = {
    subscriptionCount: 0,
    messageCount: 0,
    averageLatency: 0,
    errorCount: 0,
    lastError: null as Error | null,
    memoryUsage: 0
  };

  static incrementSubscription() {
    this.metrics.subscriptionCount++;
    this.logMetrics();
  }

  static decrementSubscription() {
    this.metrics.subscriptionCount = Math.max(0, this.metrics.subscriptionCount - 1);
    this.logMetrics();
  }

  static recordMessage(latency?: number) {
    this.metrics.messageCount++;
    
    if (latency) {
      this.metrics.averageLatency = 
        (this.metrics.averageLatency + latency) / 2;
    }

    // Log warning if too many messages
    if (this.metrics.messageCount % 100 === 0) {
      console.warn(`📊 Realtime: ${this.metrics.messageCount} messages processed`);
    }
  }

  static recordError(error: Error) {
    this.metrics.errorCount++;
    this.metrics.lastError = error;
    console.error('❌ Realtime error:', error);
  }

  static updateMemoryUsage() {
    if ('memory' in performance) {
      this.metrics.memoryUsage = (performance as any).memory.usedJSHeapSize / 1024 / 1024; // MB
    }
  }

  static getMetrics() {
    this.updateMemoryUsage();
    return { ...this.metrics };
  }

  private static logMetrics() {
    if (this.metrics.subscriptionCount > 50) {
      console.warn(`⚠️ High subscription count: ${this.metrics.subscriptionCount}`);
    }
  }

  static reset() {
    this.metrics = {
      subscriptionCount: 0,
      messageCount: 0,
      averageLatency: 0,
      errorCount: 0,
      lastError: null,
      memoryUsage: 0
    };
  }
}

// Connection health checker
export class ConnectionHealthChecker {
  private static isMonitoring = false;
  private static healthCheckInterval: NodeJS.Timeout | null = null;
  private static listeners = new Set<(healthy: boolean) => void>();

  static startMonitoring(intervalMs = 30000) {
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    this.healthCheckInterval = setInterval(() => {
      this.checkHealth();
    }, intervalMs);

    console.log('🔄 Started realtime connection health monitoring');
  }

  static stopMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.isMonitoring = false;
    console.log('⏹️ Stopped realtime connection health monitoring');
  }

  static onHealthChange(callback: (healthy: boolean) => void) {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private static async checkHealth() {
    try {
      // Simple health check - try to create and immediately remove a channel
      const testChannel = supabase.channel('health-check-' + Date.now());
      
      const healthPromise = new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          resolve(false);
        }, 5000);

        testChannel.subscribe((status) => {
          clearTimeout(timeout);
          resolve(status === 'SUBSCRIBED');
          supabase.removeChannel(testChannel);
        });
      });

      const isHealthy = await healthPromise;
      this.notifyListeners(isHealthy);
      
      if (!isHealthy) {
        console.warn('⚠️ Realtime connection health check failed');
      }
    } catch (error) {
      console.error('❌ Error during health check:', error);
      this.notifyListeners(false);
    }
  }

  private static notifyListeners(healthy: boolean) {
    this.listeners.forEach(callback => {
      try {
        callback(healthy);
      } catch (error) {
        console.error('Error in health change callback:', error);
      }
    });
  }
}

// Subscription priority manager
export class SubscriptionPriorityManager {
  private static priorities = new Map<string, number>();
  private static maxLowPriority = 5;
  private static maxMediumPriority = 15;
  private static maxHighPriority = 30;

  static canSubscribe(key: string, priority: 'low' | 'medium' | 'high'): boolean {
    const currentCount = this.getCountByPriority(priority);
    const limit = this.getLimitByPriority(priority);
    
    if (currentCount >= limit) {
      console.warn(`❌ Subscription limit reached for ${priority} priority (${currentCount}/${limit})`);
      return false;
    }

    return true;
  }

  static registerSubscription(key: string, priority: 'low' | 'medium' | 'high') {
    const priorityValue = this.getPriorityValue(priority);
    this.priorities.set(key, priorityValue);
    
    console.log(`📝 Registered ${priority} priority subscription: ${key}`);
  }

  static unregisterSubscription(key: string) {
    this.priorities.delete(key);
    console.log(`🗑️ Unregistered subscription: ${key}`);
  }

  private static getPriorityValue(priority: 'low' | 'medium' | 'high'): number {
    switch (priority) {
      case 'low': return 1;
      case 'medium': return 2;
      case 'high': return 3;
      default: return 2;
    }
  }

  private static getCountByPriority(priority: 'low' | 'medium' | 'high'): number {
    const priorityValue = this.getPriorityValue(priority);
    return Array.from(this.priorities.values()).filter(p => p === priorityValue).length;
  }

  private static getLimitByPriority(priority: 'low' | 'medium' | 'high'): number {
    switch (priority) {
      case 'low': return this.maxLowPriority;
      case 'medium': return this.maxMediumPriority;
      case 'high': return this.maxHighPriority;
      default: return this.maxMediumPriority;
    }
  }

  static getStats() {
    return {
      low: this.getCountByPriority('low'),
      medium: this.getCountByPriority('medium'),
      high: this.getCountByPriority('high'),
      total: this.priorities.size
    };
  }
}

export const batchUpdateManager = BatchUpdateManager.getInstance();